import axios from 'axios'
import { createSlice } from '@reduxjs/toolkit'
import { API, expressAPI } from 'src/utils/interceptor'
import Sentry from 'src/utils/sentry'
import {
  RejectedNotification,
  SuccessNotification,
} from '../notifications/SuccessNotification'

const initialState = {
  accounts: [],
  taxRates: [],
  contacts: [],
  selectedOrganization: null,
  isLoading: false,
  error: null,
  accountsLoading: false,
  contactsLoading: false,
  trackingCategories: [],
  trackingMapping: { trackingCategoryId: '', trackingOptionId: '' },
  trackingLoading: false,
  trackingSaving: false,
  linkableVenues: [],
  linkableVenuesLoading: false,
  linkedVenues: [],
  linkedVenuesLoading: false,
  linkingVenues: false,
}

let contactsSearchCancelSource = null

const xeroIntegrationSlice = createSlice({
  name: 'xeroIntegration',
  initialState,
  reducers: {
    setAccounts: (state, action) => {
      state.accounts = action.payload
    },
    setTaxRates: (state, action) => {
      state.taxRates = action.payload
    },
    setContacts: (state, action) => {
      state.contacts = action.payload
    },
    setSelectedOrganization: (state, action) => {
      state.selectedOrganization = action.payload
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload
    },
    setAccountsLoading: (state, action) => {
      state.accountsLoading = action.payload
    },
    setContactsLoading: (state, action) => {
      state.contactsLoading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
    },
    clearXeroData: state => {
      state.accounts = []
      state.taxRates = []
      state.contacts = []
      state.selectedOrganization = null
      state.error = null
    },
    setLinkableVenues: (state, action) => {
      state.linkableVenues = action.payload
    },
    setLinkableVenuesLoading: (state, action) => {
      state.linkableVenuesLoading = action.payload
    },
    setLinkedVenues: (state, action) => {
      state.linkedVenues = action.payload
    },
    setLinkedVenuesLoading: (state, action) => {
      state.linkedVenuesLoading = action.payload
    },
    setLinkingVenues: (state, action) => {
      state.linkingVenues = action.payload
    },
    setTrackingCategories: (state, action) => {
      state.trackingCategories = action.payload || []
    },
    setTrackingMapping: (state, action) => {
      state.trackingMapping = action.payload || {
        trackingCategoryId: '',
        trackingOptionId: '',
      }
    },
    setTrackingLoading: (state, action) => {
      state.trackingLoading = action.payload
    },
    setTrackingSaving: (state, action) => {
      state.trackingSaving = action.payload
    },
  },
})

export const {
  setAccounts,
  setTaxRates,
  setContacts,
  setSelectedOrganization,
  setLoading,
  setAccountsLoading,
  setContactsLoading,
  setError,
  clearXeroData,
  setLinkableVenues,
  setLinkableVenuesLoading,
  setLinkedVenues,
  setLinkedVenuesLoading,
  setLinkingVenues,
  setTrackingCategories,
  setTrackingMapping,
  setTrackingLoading,
  setTrackingSaving,
} = xeroIntegrationSlice.actions

export const fetchXeroTrackingCategories =
  (venueId, organizationId) => async dispatch => {
    try {
      dispatch(setTrackingLoading(true))
      dispatch(setError(null))

      const res = await expressAPI.get(
        `venue/${venueId}/xero/tracking-categories?organizationId=${encodeURIComponent(
          organizationId,
        )}`,
      )

      dispatch(setTrackingCategories(res?.data?.trackingCategories || []))
      dispatch(
        setTrackingMapping(
          res?.data?.trackingMapping || {
            trackingCategoryId: '',
            trackingOptionId: '',
          },
        ),
      )

      return res?.data
    } catch (error) {
      console.error('Error fetching Xero tracking categories:', error)
      dispatch(setError('Failed to fetch tracking categories'))
      Sentry.captureException(error)
      return null
    } finally {
      dispatch(setTrackingLoading(false))
    }
  }

export const refreshXeroTrackingCategories =
  (venueId, organizationId) => async dispatch => {
    try {
      dispatch(setTrackingLoading(true))
      dispatch(setError(null))

      const res = await expressAPI.post(
        `venue/${venueId}/xero/tracking-categories/refresh`,
        { organizationId },
      )

      dispatch(setTrackingCategories(res?.data?.trackingCategories || []))

      SuccessNotification({
        message: 'Tracking categories refreshed',
        description: `Loaded ${
          res?.data?.trackingCategories?.length || 0
        } categories`,
      })

      return res?.data
    } catch (error) {
      console.error('Error refreshing Xero tracking categories:', error)
      dispatch(setError('Failed to refresh tracking categories'))
      Sentry.captureException(error)
      RejectedNotification({
        message: 'Refresh failed',
        description: 'Could not refresh tracking categories from Xero',
      })
      return null
    } finally {
      dispatch(setTrackingLoading(false))
    }
  }

export const saveXeroTrackingMapping =
  (venueId, organizationId, trackingCategoryId, trackingOptionId) =>
  async dispatch => {
    try {
      dispatch(setTrackingSaving(true))
      dispatch(setError(null))

      const res = await expressAPI.post(
        `venue/${venueId}/xero/tracking-mapping`,
        {
          organizationId,
          trackingCategoryId,
          trackingOptionId,
        },
      )

      dispatch(
        setTrackingMapping(
          res?.data?.trackingMapping || {
            trackingCategoryId: '',
            trackingOptionId: '',
          },
        ),
      )

      SuccessNotification({
        message: 'Tracking mapping saved',
        description:
          trackingCategoryId && trackingOptionId
            ? 'Tracking will be applied to Xero line items'
            : 'Tracking not applied',
      })

      return res?.data
    } catch (error) {
      console.error('Error saving Xero tracking mapping:', error)
      dispatch(setError('Failed to save tracking mapping'))
      Sentry.captureException(error)
      RejectedNotification({
        message: 'Save failed',
        description: 'Could not save tracking mapping',
      })
      return null
    } finally {
      dispatch(setTrackingSaving(false))
    }
  }

// Fetch accounts for a specific organization
export const fetchXeroAccounts =
  (organizationId, venueId) => async dispatch => {
    try {
      dispatch(setAccountsLoading(true))
      dispatch(setError(null))

      const accounts = await API.get(
        'api2',
        `venue/${venueId}/xero/accounts?organizationId=${organizationId}`,
      )

      const data = accounts?.data || []
      dispatch(setAccounts(data))
      return data
    } catch (error) {
      console.error('Error fetching Xero accounts:', error)
      dispatch(setError('Failed to fetch accounts'))
      const status = error?.response?.status
      if (status !== 429 && error?.code !== 'ECONNABORTED') {
        Sentry.captureException(error)
      }
      RejectedNotification({
        message: status === 429 ? 'Xero is busy' : 'Failed to load accounts',
        description:
          status === 429
            ? 'Too many Xero requests — wait a moment and try again.'
            : 'There was an error loading accounts',
      })
      return null
    } finally {
      dispatch(setAccountsLoading(false))
    }
  }

export const fetchXeroTaxRates =
  (organizationId, venueId) => async dispatch => {
    try {
      const taxRates = await API.get(
        'api2',
        `venue/${venueId}/xero/tax-rates?organizationId=${organizationId}`,
      )
      dispatch(setTaxRates(taxRates?.data || []))
    } catch (error) {
      // 429 / timeout is expected under Xero rate limits — don't Sentry spam.
      console.warn('Could not load Xero tax rates', error)
      dispatch(setTaxRates([]))
    }
  }

// Fetch contacts for a specific organization
export const fetchXeroContacts =
  (organizationId, venueId) => async dispatch => {
    try {
      dispatch(setContactsLoading(true))
      dispatch(setError(null))

      const contacts = await API.get(
        'api2',
        `venue/${venueId}/xero/contacts?organizationId=${organizationId}`,
      )

      const data = contacts?.data || []
      dispatch(setContacts(data))
      return data
    } catch (error) {
      console.error('Error fetching Xero contacts:', error)
      dispatch(setError('Failed to fetch contacts'))
      const status = error?.response?.status
      if (status !== 429 && error?.code !== 'ECONNABORTED') {
        Sentry.captureException(error)
      }
      RejectedNotification({
        message: status === 429 ? 'Xero is busy' : 'Failed to load contacts',
        description:
          status === 429
            ? 'Too many Xero requests — wait a moment and try again.'
            : 'There was an error loading contacts',
      })
      return null
    } finally {
      dispatch(setContactsLoading(false))
    }
  }

// Search Xero contacts with cancellation of previous in-flight request
export const searchXeroContacts =
  (organizationId, venueId, query) => async dispatch => {
    // Cancel any previous search request before starting a new one
    if (contactsSearchCancelSource) {
      contactsSearchCancelSource.cancel('Cancelled previous contacts search')
    }

    contactsSearchCancelSource = axios.CancelToken.source()

    try {
      dispatch(setContactsLoading(true))
      dispatch(setError(null))

      const res = await expressAPI.get(
        `venue/${venueId}/xero/contacts/search?organizationId=${organizationId}&q=${encodeURIComponent(
          query,
        )}`,
        {
          cancelToken: contactsSearchCancelSource.token,
        },
      )

      dispatch(setContacts(res?.data?.data || []))
    } catch (error) {
      // Ignore cancellations; only handle real errors
      if (axios.isCancel(error)) {
        // Swallow cancellation error
        return
      }

      console.error('Error searching Xero contacts:', error)
      dispatch(setError('Failed to search contacts'))
      const status = error?.response?.status
      if (status !== 429 && error?.code !== 'ECONNABORTED') {
        Sentry.captureException(error)
      }
    } finally {
      dispatch(setContactsLoading(false))
    }
  }

// Fetch accounts + tax rates sequentially for one Xero org.
// Do not bulk-load contacts — screens already use searchXeroContacts.
// Parallel org calls were triggering Xero 429s (and Sentry timeout noise).
export const fetchXeroOrganizationData =
  (organizationId, venueId) => async dispatch => {
    try {
      dispatch(setLoading(true))
      dispatch(setError(null))
      dispatch(setContacts([]))

      const accounts = await dispatch(
        fetchXeroAccounts(organizationId, venueId),
      )
      await dispatch(fetchXeroTaxRates(organizationId, venueId))

      dispatch(setSelectedOrganization(organizationId))
      if (accounts == null) {
        // Do not toast — Invoices screen shows an inline warning; upload stays usable.
        dispatch(setError('Xero accounts unavailable'))
      } else {
        dispatch(setError(null))
        SuccessNotification({
          key: `xero-org-data-${organizationId}`,
          message: 'Accounts loaded',
          description: `Loaded ${
            accounts?.length || 0
          } accounts. Search to find contacts.`,
        })
      }
    } catch (error) {
      console.error('Error fetching Xero organization data:', error)
      dispatch(setError('Failed to fetch organization data'))
      Sentry.captureException(error)
    } finally {
      dispatch(setLoading(false))
    }
  }

// Create a new contact in Xero
export const createXeroContact =
  (contactData, organizationId, venueId) => async dispatch => {
    try {
      dispatch(setContactsLoading(true))
      dispatch(setError(null))

      const contact = await API.post('api2', `venue/${venueId}/xero/contacts`, {
        body: {
          contactData,
          organizationId,
        },
      })

      SuccessNotification({
        message: 'Contact created successfully',
        description: 'The contact has been created in Xero',
      })

      // Refresh contacts list
      dispatch(fetchXeroContacts(organizationId, venueId))

      return contact.contact
    } catch (error) {
      console.error('Error creating Xero contact:', error)
      dispatch(setError('Failed to create contact'))
      Sentry.captureException(error)
      RejectedNotification({
        message: 'Failed to create contact',
        description: 'There was an error creating the contact',
      })
      return null
    } finally {
      dispatch(setContactsLoading(false))
    }
  }

// Fetch linkable venues for multi-venue connection
export const fetchLinkableVenues = (venueId, username) => async dispatch => {
  try {
    dispatch(setLinkableVenuesLoading(true))
    dispatch(setError(null))

    if (!username) {
      console.error('Username is required for fetchLinkableVenues')
      return null
    }

    const response = await expressAPI.get(
      `venue/${venueId}/xero/linkable-venues?username=${encodeURIComponent(
        username,
      )}`,
    )

    dispatch(setLinkableVenues(response?.data?.venues || []))

    return response?.data
  } catch (error) {
    console.error('Error fetching linkable venues:', error)
    dispatch(setError('Failed to fetch linkable venues'))
    Sentry.captureException(error)
    RejectedNotification({
      message: 'Failed to load venues',
      description: 'There was an error loading linkable venues',
    })
    return null
  } finally {
    dispatch(setLinkableVenuesLoading(false))
  }
}

// Link multiple venues to Xero connection
export const linkVenuesToXero = (venueId, venueIds) => async dispatch => {
  try {
    dispatch(setLinkingVenues(true))
    dispatch(setError(null))

    const response = await expressAPI.post(
      `venue/${venueId}/xero/link-venues`,
      {
        venueIds,
      },
    )

    SuccessNotification({
      message: 'Venues linked successfully',
      description: `${
        response?.data?.linkedCount || 0
      } venue(s) have been linked to Xero`,
    })

    return response?.data
  } catch (error) {
    console.error('Error linking venues to Xero:', error)
    dispatch(setError('Failed to link venues'))
    Sentry.captureException(error)

    const errorMsg =
      error.response?.data?.message || 'There was an error linking venues'
    RejectedNotification({
      message: 'Failed to link venues',
      description: errorMsg,
    })
    return null
  } finally {
    dispatch(setLinkingVenues(false))
  }
}

// Get linked venues for disconnect warning
export const fetchLinkedVenues = venueId => async dispatch => {
  try {
    dispatch(setLinkedVenuesLoading(true))
    dispatch(setError(null))

    const response = await expressAPI.get(`venue/${venueId}/xero/linked-venues`)

    dispatch(setLinkedVenues(response?.data?.linkedVenues || []))

    return response?.data
  } catch (error) {
    console.error('Error fetching linked venues:', error)
    dispatch(setError('Failed to fetch linked venues'))
    Sentry.captureException(error)
    return null
  } finally {
    dispatch(setLinkedVenuesLoading(false))
  }
}

export const fetchXeroHealth = venueId => async () => {
  try {
    const response = await expressAPI.get(`venue/${venueId}/xero/health`)
    return response?.data || null
  } catch (error) {
    console.error('Error fetching Xero health:', error)
    Sentry.captureException(error)
    return null
  }
}

export const fetchXeroUnsentOrders =
  (venueId, { from, to, includeUnreconciled = false } = {}) =>
  async () => {
    try {
      const params = new URLSearchParams()
      if (from) params.set('from', from)
      if (to) params.set('to', to)
      if (includeUnreconciled) params.set('includeUnreconciled', 'true')
      const qs = params.toString()
      const response = await expressAPI.get(
        `venue/${venueId}/xero/unsent-orders${qs ? `?${qs}` : ''}`,
      )
      return response?.data || null
    } catch (error) {
      console.error('Error fetching Xero unsent orders:', error)
      Sentry.captureException(error)
      return null
    }
  }

export default xeroIntegrationSlice
